import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import {
  meetingRecorderApi,
  meetingRecorderReadinessKey,
  rememberRecordingConsent,
} from '@/modules/meeting-recorder'
import { useOrganizationStore } from '@/store/organization.store'
import { useRecordingConsentStore } from '@/store/recording-consent.store'
import { ShieldCheckIcon } from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { MEETING_RECORDING_CONSENT_COPY } from '@scrambl/shared'

/**
 * First-use recording notice. Mount ONCE per window (MainLayout, tray): every
 * start path awaits useRecordingConsentStore.request(), and this host shows
 * the notice and records the acknowledgement server-side before resolving.
 *
 * Copy approved by legal 2026-09-26 (docs/PLAN-meeting-recorder-launch.md).
 */
export function MeetingRecordingConsentHost() {
  const isOpen = useRecordingConsentStore(state => state.isOpen)
  const surface = useRecordingConsentStore(state => state.surface)
  const settle = useRecordingConsentStore(state => state.settle)
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const queryClient = useQueryClient()
  const [isChecked, setIsChecked] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const close = (acknowledged: boolean) => {
    setIsChecked(false)
    setError(null)
    settle(acknowledged)
  }

  const handleContinue = async () => {
    if (!isChecked || isSaving) return
    setIsSaving(true)
    setError(null)
    try {
      await meetingRecorderApi.acknowledgeConsent(surface)
      rememberRecordingConsent(organizationId)
      await queryClient.invalidateQueries({
        queryKey: meetingRecorderReadinessKey(organizationId),
      })
      close(true)
      toast.success("You won't see this notice again", {
        description:
          'Each new recording starts with a short reminder to tell participants.',
      })
    } catch {
      setError("We couldn't save your acknowledgment. Try again.")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <DialogPattern
      open={isOpen}
      onOpenChange={open => {
        if (!open && !isSaving) close(false)
      }}
      title={MEETING_RECORDING_CONSENT_COPY.title}
      description={MEETING_RECORDING_CONSENT_COPY.intro}
      icon={<ShieldCheckIcon className="h-5 w-5 text-primary" />}
      size="md"
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => close(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleContinue()}
            disabled={!isChecked || isSaving}
          >
            {MEETING_RECORDING_CONSENT_COPY.confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-foreground/90">
          {MEETING_RECORDING_CONSENT_COPY.points.map(point => (
            <li key={point}>{point}</li>
          ))}
        </ul>
        <label className="flex cursor-pointer items-start gap-2.5 rounded-md border bg-muted/40 px-3 py-2.5">
          <Checkbox
            checked={isChecked}
            onCheckedChange={checked => setIsChecked(checked === true)}
            aria-label={MEETING_RECORDING_CONSENT_COPY.acknowledgeLabel}
            className="mt-0.5"
          />
          <span className="text-sm font-medium leading-snug">
            {MEETING_RECORDING_CONSENT_COPY.acknowledgeLabel}
          </span>
        </label>
        <p className="text-xs text-muted-foreground">
          You&apos;ll only see this once. After that, each recording starts with
          a short reminder.
        </p>
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
    </DialogPattern>
  )
}
